import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@omni/ui/components/Button/button.component";
import { cn } from "@omni/utils/styles";
import { FormProvider } from "react-hook-form";

import { FormTouchedCallback } from "@/components/Form/RHF/Callbacks/formFieldCallbacks.component";
import { PasswordField } from "@/components/Form/RHF/FormFields/PasswordField/passwordField.component";
import { TextField } from "@/components/Form/RHF/FormFields/TextField/Textfield.component";
import { useOmniForm } from "@/components/Form/RHF/Utils/useOmniForm";
import { Card } from "@/components/General/Card/card.component";
import { CardBody } from "@/components/General/Card/cardBody.component";
import { CardFooter } from "@/components/General/Card/cardFooter.component";
import { CardHeader } from "@/components/General/Card/cardHeader.component";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import * as RegistrationStepTracker from "@/services/tracking/trackers/tools/registration/registrationStep.tracker";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";

import {
    RegisterFormValidationSchema,
    RegisterFormValues
} from "./registerForm.schema";

const FORM_ID = "registerForm";

type RegisterFormProps = {
    title: string;
    buttonText: string;
    isSubmitting: boolean;
    isOpen: boolean;
    disclaimer: string;
    onSubmit: (
        firstName: string,
        lastName: string,
        email: string,
        password: string
    ) => void;
    updateCardToggle: (isOpen: boolean) => void;
};

const RegisterForm = ({
    title,
    buttonText,
    isSubmitting,
    isOpen,
    disclaimer,
    onSubmit,
    updateCardToggle
}: RegisterFormProps) => {
    const { t } = useGranite();

    const handleSubmit = ({
        firstName,
        lastName,
        email,
        password
    }: RegisterFormValues) => onSubmit(firstName, lastName, email, password);

    const handleCardToggle = (e: any) => {
        e.preventDefault();
        updateCardToggle(!isOpen);
    };

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            firstName: "",
            lastName: "",
            email: "",
            password: ""
        },
        resolver: yupResolver(RegisterFormValidationSchema)
    });

    return (
        <Card>
            <CardHeader
                className={cn("tw:px-5 tw:text-xl tw:border-0 tw:md:hidden")}
                style={{ backgroundColor: "inherit" }}
            >
                <a
                    href="#"
                    onClick={(e) => handleCardToggle(e)}
                    className={cn("tw:sm:hidden tw:m-4")}
                >
                    <Row>
                        <div className={cn("col-9 tw:m-0 tw:py-0")}>
                            <div>
                                <h3 className={cn("tw:m-0")}>{title}</h3>
                            </div>
                        </div>
                        <div className={cn("col-3 tw:text-right")}>
                            <h3 className={cn("tw:m-0")}>
                                <i
                                    className={`las la-angle-${
                                        isOpen ? "down" : "right"
                                    }`}
                                />
                            </h3>
                        </div>
                    </Row>
                </a>
            </CardHeader>
            <div
                className={cn(
                    "checkoutGuest tw:sm:block collapse",
                    isOpen && "show"
                )}
            >
                <FormInfoProvider
                    formId={FORM_ID}
                    withNeverbounce={true}
                >
                    {({ initialized }) => (
                        <FormProvider {...methods}>
                            <form
                                id={FORM_ID}
                                onSubmit={methods.handleSubmit(handleSubmit)}
                            >
                                <FormTouchedCallback
                                    callback={
                                        RegistrationStepTracker.trackStart
                                    }
                                />
                                <CardBody className={cn("tw:max-sm:py-0")}>
                                    <h2 className={cn("tw:hidden tw:sm:block")}>
                                        {title}
                                    </h2>
                                    <Row>
                                        <Col md={12}>
                                            <TextField<RegisterFormValues>
                                                test="register-input-firstname"
                                                name={"firstName"}
                                                label={t(
                                                    "registerForm.label.firstname"
                                                )}
                                                required
                                                autoComplete="firstName"
                                                placeholder={t(
                                                    "general.form.firstName.placeholder"
                                                )}
                                                disabled={
                                                    !initialized || isSubmitting
                                                }
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={12}>
                                            <TextField<RegisterFormValues>
                                                test="register-input-lastname"
                                                name={"lastName"}
                                                label={t(
                                                    "registerForm.label.lastname"
                                                )}
                                                required
                                                autoComplete="lastName"
                                                placeholder={t(
                                                    "general.form.lastName.placeholder"
                                                )}
                                                disabled={
                                                    !initialized || isSubmitting
                                                }
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={12}>
                                            <TextField<RegisterFormValues>
                                                test="register-input-email"
                                                name="email"
                                                label={t(
                                                    "general.form.email.label"
                                                )}
                                                required
                                                autoComplete="email"
                                                placeholder={t(
                                                    "general.form.email.placeholder"
                                                )}
                                                disabled={
                                                    !initialized || isSubmitting
                                                }
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={12}>
                                            <PasswordField<RegisterFormValues>
                                                test="register-input-password"
                                                name="password"
                                                label={t(
                                                    "general.form.password.label"
                                                )}
                                                required
                                                autoComplete="new-password"
                                                placeholder={t(
                                                    "general.form.password.placeholder"
                                                )}
                                                disabled={
                                                    !initialized || isSubmitting
                                                }
                                            />
                                        </Col>
                                    </Row>
                                </CardBody>
                                <CardFooter
                                    className={cn(
                                        "tw:pb-5 tw:border-0 tw:pt-0"
                                    )}
                                    style={{ backgroundColor: "inherit" }}
                                >
                                    <div
                                        className={cn(
                                            "tw:font-light tw:text-sm"
                                        )}
                                        dangerouslySetInnerHTML={{
                                            __html: disclaimer
                                        }}
                                    />
                                    <Button
                                        test="register-button-submit"
                                        type={"submit"}
                                        size={"lg"}
                                        loading={isSubmitting}
                                        disabled={!initialized}
                                    >
                                        {buttonText}
                                    </Button>
                                </CardFooter>
                            </form>
                        </FormProvider>
                    )}
                </FormInfoProvider>
            </div>
        </Card>
    );
};

export default RegisterForm;
